<template>
  <div class="section overflow-hidden position-relative" id="wrapper">
    <!-- Page Content Wrapper Start -->
    <div class="page-content-wrapper padding-left-0">
      <div class="container-fluid custom-container">

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loader"></div>
          <p>Loading requests...</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="error-state">
          <p class="text-danger">{{ error }}</p>
          <button class="btn btn-primary" @click="loadRequests">Retry</button>
        </div>

        <!-- Main Content -->
        <div v-if="!loading && !error" class="section section-padding mt-n10 padding-top-50">
          
          <!-- Page Header -->
          <div class="page-header">
            <h2 class="section-title">Package Requests Management</h2>
            <p class="section-subtitle">Review and manage custom package requests from organizations</p>
          </div>

          <!-- Active Requests Section -->
          <div class="requests-section">
            <div class="section-header">
              <h3>
                <i class="icofont-clock-time"></i> Pending Requests 
                <span class="badge badge-primary">{{ pendingRequests.length }}</span>
              </h3>
            </div>

            <!-- No Active Requests -->
            <div v-if="pendingRequests.length === 0" class="no-requests">
              <i class="icofont-inbox"></i>
              <p>No pending requests at the moment</p>
            </div>

            <!-- Request Cards Grid -->
            <div v-else class="requests-grid">
              <div 
                v-for="request in pendingRequests" 
                :key="request._id" 
                class="request-card pending"
              >
                <div class="card-header">
                  <div class="org-icon">{{ getInitials(request.name) }}</div>
                  <div class="org-info">
                    <h4>{{ request.name }}</h4>
                    <p class="date-info">
                      <i class="icofont-calendar"></i> 
                      {{ formatDate(request.date) }}
                    </p>
                  </div>
                  <span class="status-badge pending">Pending</span>
                </div>
                
                <div class="card-body">
                  <div class="info-row">
                    <i class="icofont-email"></i>
                    <span>{{ request.email }}</span>
                  </div>
                  <div class="info-row">
                    <i class="icofont-phone"></i>
                    <span>{{ request.phone }}</span>
                  </div>
                  <div class="info-row">
                    <i class="icofont-users-alt-4"></i>
                    <span>{{ request.users }} Candidates</span>
                  </div>
                  <div class="info-row">
                    <i class="icofont-file-document"></i>
                    <span>{{ request.assessments }} Assessments × {{ request.questions }} Questions</span>
                  </div>
                </div>

                <div class="card-footer">
                  <button 
                    class="btn btn-primary btn-sm" 
                    @click="viewRequest(request)"
                  >
                    View Request
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Completed Requests Section -->
          <div class="requests-section completed-section">
            <div class="section-header">
              <h3>
                <i class="icofont-check-circled"></i> Completed Requests 
                <span class="badge badge-secondary">{{ completedRequests.length }}</span>
              </h3>
            </div>

            <!-- No Completed Requests -->
            <div v-if="completedRequests.length === 0" class="no-requests">
              <i class="icofont-inbox"></i>
              <p>No completed requests yet</p>
            </div>

            <!-- Completed Request Cards Grid -->
            <div v-else class="requests-grid">
              <div 
                v-for="request in completedRequests" 
                :key="request._id" 
                class="request-card"
                :class="request.status"
              >
                <div class="card-header">
                  <div class="org-icon completed">{{ getInitials(request.name) }}</div>
                  <div class="org-info">
                    <h4>{{ request.name }}</h4>
                    <p class="date-info">
                      <i class="icofont-calendar"></i> 
                      {{ formatDate(request.processed_date || request.date) }}
                    </p>
                  </div>
                  <span class="status-badge" :class="request.status">
                    {{ request.status === 'accepted' ? 'Accepted' : 'Rejected' }}
                  </span>
                </div>
                
                <div class="card-body">
                  <div class="info-row">
                    <i class="icofont-email"></i>
                    <span>{{ request.email }}</span>
                  </div>
                  <div class="info-row">
                    <i class="icofont-users-alt-4"></i>
                    <span>{{ request.users }} Candidates</span>
                  </div>
                  <div v-if="request.status === 'rejected' && request.rejection_reason" class="rejection-reason">
                    <strong>Rejection Reason:</strong>
                    <p>{{ request.rejection_reason }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- View/Edit Request Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-container">
        <div class="modal-header">
          <h3>Package Request Details</h3>
          <button class="close-btn" @click="closeModal">
            <i class="icofont-close"></i>
          </button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="createPackage">
            <!-- Organization Details Section -->
            <div class="form-section">
              <h4 class="section-title">
                <i class="icofont-building"></i> Organization Details
              </h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Organization Name</label>
                  <input type="text" v-model="selectedRequest.name" readonly class="form-control">
                </div>
                <div class="form-group">
                  <label>Contact Email</label>
                  <input type="email" v-model="selectedRequest.email" readonly class="form-control">
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Phone Number</label>
                  <input type="text" v-model="selectedRequest.phone" readonly class="form-control">
                </div>
                <div class="form-group">
                  <label>Number of Candidates</label>
                  <input type="number" v-model="selectedRequest.users" readonly class="form-control">
                </div>
              </div>
            </div>

            <!-- Package Requirements Section -->
            <div class="form-section">
              <h4 class="section-title">
                <i class="icofont-package"></i> Package Requirements
              </h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Assessments per Candidate</label>
                  <input type="number" v-model="selectedRequest.assessments" readonly class="form-control">
                </div>
                <div class="form-group">
                  <label>Questions per Assessment</label>
                  <input type="number" v-model="selectedRequest.questions" readonly class="form-control">
                </div>
              </div>
              <div class="form-group full-width">
                <label>Additional Message</label>
                <textarea v-model="selectedRequest.message" readonly class="form-control" rows="3"></textarea>
              </div>
            </div>

            <!-- Admin Input Section -->
            <div class="form-section admin-section">
              <h4 class="section-title">
                <i class="icofont-ui-settings"></i> Admin Configuration
              </h4>
              <div class="form-row">
                <div class="form-group">
                  <label>Package Price (£) <span class="required">*</span></label>
                  <input 
                    type="number" 
                    v-model="packageData.price" 
                    required 
                    min="0"
                    placeholder="Enter price"
                    class="form-control"
                  >
                </div>
                <div class="form-group">
                  <label>Test Duration (minutes) <span class="required">*</span></label>
                  <input 
                    type="number" 
                    v-model="packageData.duration" 
                    required 
                    min="1"
                    placeholder="e.g., 60"
                    class="form-control"
                  >
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Mock Test Attempts Allowed <span class="required">*</span></label>
                  <input 
                    type="number" 
                    v-model="packageData.mock_attempts" 
                    required 
                    min="1"
                    placeholder="e.g., 5"
                    class="form-control"
                  >
                </div>
                <div class="form-group">
                  <label>Total Questions in Pool <span class="required">*</span></label>
                  <input 
                    type="number" 
                    v-model="packageData.total_questions" 
                    required 
                    min="1"
                    placeholder="Total questions available"
                    class="form-control"
                  >
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="modal-actions">
              <button 
                type="button" 
                class="btn btn-danger padding-top-0 padding-bottom-0" 
                @click="showRejectModal = true"
              >
                <i class="icofont-close-circled"></i> Reject Request
              </button>
              <button 
                type="submit" 
                class="btn btn-success padding-top-0 padding-bottom-0" 
                :disabled="submitting"
              >
                <span v-if="!submitting">
                  <i class="icofont-check-circled"></i> Create Package
                </span>
                <span v-else>
                  <div class="btn-loader"></div> Creating...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Reject Confirmation Modal -->
    <div v-if="showRejectModal" class="modal-overlay" @click.self="showRejectModal = false">
      <div class="modal-container small">
        <div class="modal-header reject">
          <h3>Reject Package Request</h3>
          <button class="close-btn" @click="showRejectModal = false">
            <i class="icofont-close"></i>
          </button>
        </div>

        <div class="modal-body">
          <p class="warning-text">
            <i class="icofont-warning"></i>
            Are you sure you want to reject this request? An email will be sent to the organization with your reason.
          </p>
          
          <form @submit.prevent="confirmReject">
            <div class="form-group">
              <label>Reason for Rejection <span class="required">*</span></label>
              <textarea 
                v-model="rejectionReason" 
                required 
                class="form-control" 
                rows="4"
                placeholder="Please provide a detailed reason for rejection..."
              ></textarea>
            </div>

            <div class="modal-actions">
              <button 
                type="button" 
                class="btn btn-secondary" 
                @click="showRejectModal = false"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                class="btn btn-danger" 
                :disabled="submitting"
              >
                <span v-if="!submitting">
                  <i class="icofont-trash"></i> Confirm Rejection
                </span>
                <span v-else>
                  <div class="btn-loader"></div> Rejecting...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { 
  getAllRequests, 
  createPackageFromRequest, 
  rejectRequest 
} from '@/services/packageService';

export default {
  name: 'CreatePackageForm',
  data() {
    return {
      loading: false,
      error: null,
      requests: [],
      showModal: false,
      showRejectModal: false,
      selectedRequest: null,
      submitting: false,
      rejectionReason: '',
      packageData: {
        price: '',
        duration: '',
        mock_attempts: '',
        total_questions: ''
      }
    };
  },
  computed: {
    pendingRequests() {
      return this.requests.filter(r => r.status === 'pending');
    },
    completedRequests() {
      return this.requests.filter(r => r.status === 'accepted' || r.status === 'rejected');
    }
  },
  methods: {
    async loadRequests() {
      this.loading = true;
      this.error = null;

      try {
        this.requests = await getAllRequests();
      } catch (error) {
        console.error('Failed to load requests:', error);
        this.error = 'Failed to load package requests. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    viewRequest(request) {
      this.selectedRequest = { ...request };
      this.packageData = {
        price: '',
        duration: 60,
        mock_attempts: 5,
        total_questions: request.questions
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedRequest = null;
      this.packageData = {
        price: '',
        duration: '',
        mock_attempts: '',
        total_questions: ''
      };
    },

    async createPackage() {
      if (!this.packageData.price || !this.packageData.duration || !this.packageData.mock_attempts || !this.packageData.total_questions) {
        alert('Please fill all required fields');
        return;
      }

      this.submitting = true;

      try {
        const payload = {
          request_id: this.selectedRequest._id,
          org_email: this.selectedRequest.email,
          price: Number(this.packageData.price),
          duration: Number(this.packageData.duration),
          mock_attempts_allowed: Number(this.packageData.mock_attempts),
          total_questions: Number(this.packageData.total_questions),
          users_allowed: this.selectedRequest.users,
          assessments: this.selectedRequest.assessments,
          new_questions_each_time: Number(this.packageData.total_questions)
        };

        await createPackageFromRequest(payload);
        
        alert('Package created successfully! Confirmation email sent to organization.');
        this.closeModal();
        await this.loadRequests();
      } catch (error) {
        console.error('Failed to create package:', error);
        alert('Failed to create package. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    async confirmReject() {
      if (!this.rejectionReason.trim()) {
        alert('Please provide a reason for rejection');
        return;
      }

      this.submitting = true;

      try {
        await rejectRequest({
          request_id: this.selectedRequest._id,
          rejection_reason: this.rejectionReason
        });

        alert('Request rejected successfully. Notification email sent to organization.');
        this.showRejectModal = false;
        this.closeModal();
        this.rejectionReason = '';
        await this.loadRequests();
      } catch (error) {
        console.error('Failed to reject request:', error);
        alert('Failed to reject request. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    getInitials(name) {
      if (!name) return '?';
      const words = name.split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  },

  async created() {
    await this.loadRequests();
  }
};
</script>

<style scoped>
/* Loading and Error States */
.loading-state, .error-state {
  padding: 60px;
  text-align: center;
}

.loader {
  width: 50px;
  aspect-ratio: 1;
  border-radius: 50%;
  border: 6px solid #e0e0e0;
  border-right-color: #309255;
  animation: spin 1s infinite linear;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(1turn); }
}

/* Page Header */
.page-header {
  margin-bottom: 40px;
}

.section-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.section-subtitle {
  color: #666;
  font-size: 16px;
}

/* Requests Section */
.requests-section {
  margin-bottom: 50px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e0e0e0;
}

.section-header h3 {
  font-size: 22px;
  font-weight: 600;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header i {
  font-size: 24px;
  color: #309255;
}

.completed-section .section-header i {
  color: #666;
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 12px;
}

.badge-primary {
  background-color: #309255;
  color: white;
  height: 30px;
  border-radius: 50px;
  line-height: 22px;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

/* No Requests State */
.no-requests {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.no-requests i {
  font-size: 64px;
  margin-bottom: 15px;
  opacity: 0.5;
}

/* Requests Grid */
.requests-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 25px;
}

/* Request Card */
.request-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  overflow: hidden;
}

.request-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  transform: translateY(-2px);
}

.request-card.pending {
  border: 2px solid #309255;
  background-color: #e9e9e9;
}

.request-card.accepted {
  border-left: 4px solid #28a745;
  opacity: 0.85;
}

.request-card.rejected {
  border-left: 4px solid #dc3545;
  opacity: 0.85;
}

/* Card Header */
.card-header {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.org-icon {
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #309255, #28a745);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
}

.org-icon.completed {
  background: linear-gradient(135deg, #6c757d, #495057);
}

.org-info {
  flex: 1;
}

.org-info h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 5px 0;
}

.date-info {
  font-size: 13px;
  color: #666;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.pending {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.accepted {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.rejected {
  background-color: #f8d7da;
  color: #721c24;
}

/* Card Body */
.card-body {
  padding: 20px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  font-size: 14px;
  color: #333;
}

.info-row i {
  color: #309255;
  font-size: 16px;
  width: 20px;
}

.rejection-reason {
  margin-top: 15px;
  padding: 12px;
  background-color: #fff3cd;
  border-left: 3px solid #ffc107;
  border-radius: 4px;
  font-size: 13px;
}

.rejection-reason strong {
  display: block;
  margin-bottom: 5px;
  color: #856404;
}

.rejection-reason p {
  margin: 0;
  color: #666;
}

/* Card Footer */
.card-footer {
  padding: 15px 20px;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
  text-align: center;
}

/* Buttons */
.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-sm {
  padding: 8px 16px;
  font-size: 13px;
}

.btn-primary {
  background-color: #309255;
  color: white;
  height: 45px;
  font-size: 15px;
}

.btn-primary:hover {
  background-color: #267a44;
  color: white;
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:hover {
  background-color: #218838;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #c82333;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-loader {
  width: 16px;
  height: 16px;
  border: 2px solid white;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  display: inline-block;
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  overflow-y: auto;
}

/* Modal Container */
.modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3);
}

.modal-container.small {
  max-width: 500px;
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 25px 30px;
  border-bottom: 2px solid #e0e0e0;
  background: linear-gradient(135deg, #309255, #28a745);
}

.modal-header.reject {
  background: linear-gradient(135deg, #dc3545, #c82333);
}

.modal-header h3 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: white;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.3s ease;
}

.close-btn:hover {
  background: rgba(255,255,255,0.2);
}

/* Modal Body */
.modal-body {
  padding: 30px;
}

/* Form Sections */
.form-section {
  margin-bottom: 30px;
  padding-bottom: 25px;
  border-bottom: 1px solid #e0e0e0;
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section .section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-section.admin-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 2px solid #309255;
}

/* Form Layout */
.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.required {
  color: #dc3545;
  margin-left: 3px;
}

.form-control {
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: #309255;
  box-shadow: 0 0 0 3px rgba(48, 146, 85, 0.1);
}

.form-control:read-only {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

/* Warning Text */
.warning-text {
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.warning-text i {
  color: #856404;
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 25px;
  border-top: 1px solid #e0e0e0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .requests-grid {
    grid-template-columns: 1fr;
  }

  .modal-container {
    max-width: 100%;
    margin: 0;
    border-radius: 0;
    max-height: 100vh;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }

  .modal-actions .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>